/**
 * Shared render helpers for PurchaseFlow wrappers.
 *
 * Provides `renderIcon` and `renderText` implementations that map
 * PurchaseFlow abstract keys to the app's concrete SVG icons and i18n translations.
 *
 * Usage:
 *   const { renderIcon, renderText } = usePurchaseFlowRenderProps();
 *   <PurchaseFlowWrapper renderIcon={renderIcon} renderText={renderText} ... />
 */
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PurchaseSvgIcon } from '../sprites';
import { getPurchaseSpriteUrl, loadSVGSprite } from '../sprites/loadSprite';
import { appLogo } from '../../app/logo/logo';
import { PURCHASE_FLOW_ICONS, PURCHASE_FLOW_TEXT_KEYS } from '../../app/components/purchase_flow/utils/constants';
import { getAddIconNameForPOWVersion } from '../purchase_details_common/purchase_details_common_utils';
import { isMobile } from './utils';
import { log, LOG_LEVELS } from './app_logger';

const SPRITE_CONTAINER_ID = 'purchase-svg-sprite-container';

// ============================================================================
// Log Adapter
// ============================================================================

const LOG_LEVEL_MAP: Record<string, string> = {
  verbose: LOG_LEVELS.VERBOSE,
  debug: LOG_LEVELS.DEBUG,
  info: LOG_LEVELS.INFO,
  error: LOG_LEVELS.ERROR
};

/**
 * Stable module-level log adapter for PurchaseFlow wrappers.
 * Maps PurchaseFlow's abstract LogLevel strings to the app logger.
 * Safe to pass as `onLog` in config objects — referentially stable, no hooks required.
 */
export const purchaseFlowOnLog = (level: string, tag: string, message: string, data?: unknown): void => {
  log(LOG_LEVEL_MAP[level] || LOG_LEVELS.INFO, tag, message, data);
};

// ============================================================================
// Icon Mapping
// ============================================================================

/**
 * Static icon mappings — keys whose sprite id doesn't depend on runtime context.
 * Card-type icons (visa, mastercard, etc.) are passed through as-is since
 * PurchaseSvgIcon already uses the lowercased card type as the sprite id.
 */
const STATIC_ICON_ID_MAP: Record<string, string> = {
  [PURCHASE_FLOW_ICONS.BACK]: 'payment_arrow',
  [PURCHASE_FLOW_ICONS.CLOSE]: 'purchase_close_icon',
  [PURCHASE_FLOW_ICONS.ARROW_RIGHT]: 'payment_arrow',
  [PURCHASE_FLOW_ICONS.ERROR]: 'error_icon',
  [PURCHASE_FLOW_ICONS.SUCCESS]: 'purchases_success',
  [PURCHASE_FLOW_ICONS.PROCESSING_ERROR]: 'purchases_flow_error',
  [PURCHASE_FLOW_ICONS.PROCESSING_ERROR_MOBILE]: 'mobile_processing_status_error_icon',
  [PURCHASE_FLOW_ICONS.PAYPAL]: 'paypal_icon',
  [PURCHASE_FLOW_ICONS.APPLE_PAY]: 'apple_pay_icon',
  [PURCHASE_FLOW_ICONS.GOOGLE_PAY]: 'google_pay_icon',
  [PURCHASE_FLOW_ICONS.UNKNOWN_CARD]: 'unknown',
  [PURCHASE_FLOW_ICONS.ADD_GOOGLE_PAY]: 'add_gp',
  [PURCHASE_FLOW_ICONS.LOCK]: 'lock_icon',
  [PURCHASE_FLOW_ICONS.GUARANTEED_SAFE]: 'guaranteed_safe_icons',
  [PURCHASE_FLOW_ICONS.CC_INFO]: 'cc_info',
  [PURCHASE_FLOW_ICONS.VALIDATION_ERROR]: 'error_icon',
  [PURCHASE_FLOW_ICONS.PAYMENT_SOURCE_ERROR]: 'payment_source_error',
  [PURCHASE_FLOW_ICONS.DELETE]: 'delete_icon'
};

/**
 * Icons that need POW-version-aware sprite id resolution.
 * These go through `getAddIconNameForPOWVersion` at runtime.
 */
const POW_VERSIONED_BASE_MAP: Record<string, string> = {
  [PURCHASE_FLOW_ICONS.ADD_CREDIT_CARD]: 'add_cc',
  [PURCHASE_FLOW_ICONS.ADD_PAYPAL]: 'add_pp',
  [PURCHASE_FLOW_ICONS.IAP]: 'iap_icon',
  [PURCHASE_FLOW_ICONS.ADD_IAP]: 'add_iap',
  [PURCHASE_FLOW_ICONS.PLACEHOLDER]: 'placeholder'
};

/**
 * Resolves the sprite id for an abstract icon key.
 */
const getSpriteId = (icon: string): string => {
  if (STATIC_ICON_ID_MAP[icon]) return STATIC_ICON_ID_MAP[icon];
  const powBase = POW_VERSIONED_BASE_MAP[icon];
  if (powBase) return getAddIconNameForPOWVersion(powBase);
  // Dynamic card types (e.g. 'visa', 'mastercard') — pass through as-is
  return icon.toLowerCase().replace(' ', '_');
};

/**
 * Resolves an abstract icon key to a PurchaseSvgIcon element.
 * Known keys are mapped; unknown keys (dynamic card types like 'visa') are
 * passed through with lowercased, underscore-separated ids — matching
 * the credit card sprite naming convention.
 */
const purchaseFlowRenderIcon = (icon: string, className?: string): React.ReactNode => {
  if (icon === PURCHASE_FLOW_ICONS.APP_LOGO) {
    return <img className={ className } src={ appLogo() as unknown as string } alt="" />;
  }
  const spriteId = getSpriteId(icon);
  return <PurchaseSvgIcon className={ className } id={ spriteId } />;
};

// ============================================================================
// Text Mapping
// ============================================================================

/**
 * Maps PURCHASE_FLOW_TEXT_KEYS values to existing i18n translation keys.
 *
 * The purchase flow uses its own namespaced keys (e.g. 'purchaseFlow.title.paymentInformation').
 * This map translates them to the legacy keys already present in en.json
 * (e.g. 'purchase.purchase_payment_information', 'chat.additional_funds_required').
 */
const TEXT_KEY_MAP: Record<string, string | (() => string)> = {
  // Screen titles
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_PAYMENT_INFORMATION]: 'purchase.purchase_payment_information',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_ADDITIONAL_FUNDS]: 'chat.additional_funds_required',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_CLAIM_GIFT]: 'purchase.purchase_credit_affiliate_promo_popup_title',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_PURCHASE_CREDIT]: 'purchase.purchase_credit_side_menu',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_PAYMENT_METHODS]: 'purchase.use_a_different_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_ADD_PAYMENT_METHOD]: 'blueSnap.add_credit_card_title',
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_PROCESSING]: 'purchase.processing_payment',
  [PURCHASE_FLOW_TEXT_KEYS.PROCESSING_EXTERNAL_WINDOW]: 'purchase.your_payment_is_processing_in_another_window',
  [PURCHASE_FLOW_TEXT_KEYS.PROCESSING_REDIRECT_LINK]: 'purchase.redirect_here',

  // Buttons
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_PAY]: 'purchase.purchase_pay',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_START_SESSION]: 'purchase.purchase_start_session',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_ADD_GET_BONUS]: 'purchase.affiliate_promo_popup_button_title',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_ADD_GET_BONUS_SUFFIX]: 'purchase.affiliate_promo_popup_button_bonus',
  [PURCHASE_FLOW_TEXT_KEYS.PROMO_MAYBE_LATER]: 'purchase.maybe_later',
  [PURCHASE_FLOW_TEXT_KEYS.PROMO_OFFER_EXPIRES]: 'purchase.offer_expires_at_midnight_ET',

  // Details toggle
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_SHOW]: 'chat.show_details',
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_HIDE]: 'chat.hide_details',

  // Payment options
  [PURCHASE_FLOW_TEXT_KEYS.SELECT_OPTION]: 'purchase.select_option',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_APPLE_PAY]: 'purchase.add_ap',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_PAYPAL]: 'purchase.add_pp',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_CONTINUE_GOOGLE_PAY]: 'purchase.add_gp',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_SETUP_APPLE_PAY]: 'purchase.add_ap',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_ADD_CARD]: 'purchase.add_cc',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_ADD_PAYMENT_METHOD]: 'purchase.add_payment',
  [PURCHASE_FLOW_TEXT_KEYS.OPTION_IN_APP_PURCHASE]: 'purchase.add_iap',

  // Payment methods list
  [PURCHASE_FLOW_TEXT_KEYS.DEFAULT_PAYMENT_METHOD]: 'purchase.default',
  [PURCHASE_FLOW_TEXT_KEYS.NO_SAVED_METHODS]: 'purchase.you_currently_have_no_saved_payment_methods',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_NOT_SUPPORTED_MESSAGE]: 'purchase.payment_option_not_supported',
  [PURCHASE_FLOW_TEXT_KEYS.SELECT_DIFFERENT_PAYMENT_METHOD]: 'purchase.change_payment_option',

  // PAYG ineligibility (per-kind, maps to existing legacy translations)
  [PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_CARD]: 'purchase.this_card_cannot_be_used_for_zen_mode',
  [PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_PAYPAL]: 'purchase.paypal_is_not_available_for_this_service',
  [PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_GOOGLE_PAY]: 'purchase.google_pay_is_not_available_for_this_service',
  [PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_APPLE_PAY]: 'purchase.apple_pay_is_not_available_for_this_service',

  // Conditional titles
  [PURCHASE_FLOW_TEXT_KEYS.TITLE_MINIMUM_BALANCE]: 'chat.minimum_balance_required',

  // Details slot content
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_REQUIRED_FUNDS]: 'chat.required_funds',
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_REQUIRED_FUNDS_WITH_MIN]: 'chat.required_funds_with_min',
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_CURRENT_BALANCE]: 'chat.current_balance',
  [PURCHASE_FLOW_TEXT_KEYS.DETAILS_ADDITIONAL_FUNDS]: 'chat.additional_funds_required',

  // Minimum purchase note
  [PURCHASE_FLOW_TEXT_KEYS.MIN_PURCHASE_NOTE]: 'chat.purchase_min',

  // Credit card form — Labels
  [PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_NAME]: 'blueSnap.cardholder_name',
  [PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CARD_NUMBER]: 'blueSnap.card_number_label',
  [PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_EXPIRY]: 'blueSnap.expiration_label',
  [PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CVV]: 'blueSnap.security_label',
  [PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_ZIP]: 'blueSnap.zip_postal_code_label',

  // Credit card form — Validation errors
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_NAME]: 'blueSnap.enter_card_name',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_ZIP]: 'blueSnap.enter_zip',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CARD_NUMBER]: 'blueSnap.incorrect_card_number',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CVV]: 'blueSnap.incorrect_cvv_number',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_EXPIRY]: 'blueSnap.incorrect_expiration_date',

  // Credit card form — Buttons & labels
  [PURCHASE_FLOW_TEXT_KEYS.CC_SAVE_CARD]: 'blueSnap.save_card',
  [PURCHASE_FLOW_TEXT_KEYS.CC_GUARANTEED_SAFE]: 'blueSnap.guaranteed_safe',
  [PURCHASE_FLOW_TEXT_KEYS.CC_PAYG_DISCLAIMER]: 'blueSnap.zen_mode_supports_credit',

  // Credit card form — Error popup
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_TITLE]: 'purchase.add_cc_error_title',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_MESSAGE]: 'purchase.error_subtitle',
  [PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_POPUP_OK]: 'general.ok',

  // Credit card form — Tooltips
  [PURCHASE_FLOW_TEXT_KEYS.CC_TOOLTIP_EXPIRY]: 'blueSnap.tooltip_exp_text',
  [PURCHASE_FLOW_TEXT_KEYS.CC_TOOLTIP_CVV]: 'blueSnap.tooltip_cvv_text',

  // Processing screen — Error state
  [PURCHASE_FLOW_TEXT_KEYS.ERROR_TITLE]: 'paypal_purchase.error_title',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_INCREASE_SPENDING_LIMIT]: 'paypal_purchase.increase_spending_limits',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK]: 'general.ok',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_CONTINUE]: 'general.continue',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_START_CHAT]: 'chat.start_chat',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_START_VIDEO_CALL]: 'chat.start_video_call',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_START_VOICE_CALL]: 'chat.start_voice_call',
  [PURCHASE_FLOW_TEXT_KEYS.BUTTON_RETRY]: 'general.retry',

  // Processing screen — Popup blocked (Google Pay on Safari)
  [PURCHASE_FLOW_TEXT_KEYS.ERROR_POPUP_BLOCKED_TITLE]: 'google_pay.enable_google_pay',
  [PURCHASE_FLOW_TEXT_KEYS.ERROR_POPUP_BLOCKED_MESSAGE]: () => (isMobile()
    ? 'google_pay.to_use_google_pay_on_safari_you_need_to_allow_pop_ups_mobile'
    : 'google_pay.to_use_google_pay_on_safari_you_need_to_allow_pop_ups_desktop'),

  // Processing screen — Success state (cashback)
  [PURCHASE_FLOW_TEXT_KEYS.CASHBACK_EARNED]: 'paypal_purchase.purchase_earn',
  [PURCHASE_FLOW_TEXT_KEYS.CASHBACK_LABEL]: 'paypal_purchase.cashback',

  // PAYG inline validation — Error state
  [PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_TITLE]: 'credit_card_validate_processing.card_validation_failed',
  [PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_MESSAGE]: 'credit_card_validate_processing.there_was_a_failure_validating_your_card',

  // Payment sources management page
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_TITLE]: 'purchase.payment_methods',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_DETAILS_TITLE]: 'purchase.payment_method_info',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_ADD_CARD_TITLE]: 'blueSnap.add_credit_card_title',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_EMPTY]: 'purchase.you_currently_have_no_saved_payment_methods',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_ADD_NEW_PAYMENT_METHOD]: 'purchase.add_a_new_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_ADD_ANOTHER_PAYMENT_METHOD]: 'purchase.add_another_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_SET_DEFAULT]: 'purchase.set_default',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_REMOVE]: 'purchase.delete_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_CANCEL]: 'general.cancel',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_REMOVE_CONFIRM_TITLE]: 'purchase.delete_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_REMOVE_CONFIRM_MESSAGE]: 'purchase.are_you_sure_you_want_to_delete_this_payment_method',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_CANNOT_REMOVE_DEFAULT]: 'purchase.you_must_select_a_new_default_payment_method_before_removing_this_one',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_SET_DEFAULT_ERROR]: 'general.error',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_REMOVE_ERROR]: 'general.error',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_DEFAULT_UPDATED]: 'purchase.default_payment_updated',
  [PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_CONFIRM_DELETE]: 'purchase.yes_delete_it',

  // Affiliate promo success screen
  [PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_BALANCE]: 'purchase.balance',
  [PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONGRATS]: 'app_purchase.affiliate_promo_popup_congratulations',
  [PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_PREFIX]: 'purchase.if_you_need_anything_from_us',
  [PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_LINK_TEXT]: 'purchase.contact_us',
  [PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_SUFFIX]: 'purchase.any_time'
};

/**
 * Resolves a PURCHASE_FLOW_TEXT_KEYS abstract key to the concrete i18n key.
 * Useful when components need the raw i18n key (e.g. for the Trans component)
 * rather than the translated string returned by renderText.
 */
export const resolveTextKey = (key: string): string => {
  const entry = TEXT_KEY_MAP[key];
  return typeof entry === 'function' ? entry() : (entry || key);
};

// ============================================================================
// Hook
// ============================================================================

export interface PurchaseFlowRenderProps {
  renderIcon: (icon: string) => React.ReactNode;
  renderText: (key: string, params?: Record<string, string | number>) => string;
}

/**
 * Returns `renderIcon` and `renderText` implementations wired to
 * the app's PurchaseSvgIcon sprite system and react-i18next translations.
 *
 * The purchase SVG sprite sheet is loaded into a hidden container appended
 * to document.body (created once, survives component unmount/remount cycles).
 *
 * @example
 * const { renderIcon, renderText } = usePurchaseFlowRenderProps();
 * <PurchaseFlowWrapper renderIcon={renderIcon} renderText={renderText} />
 */
export const usePurchaseFlowRenderProps = (): PurchaseFlowRenderProps => {
  const { t } = useTranslation();

  // Load purchase SVG sprites on mount — container is auto-created in document.body
  useEffect(() => {
    let isCleanedUp = false;
    const loadSprites = async () => {
      if (!isCleanedUp) {
        loadSVGSprite(getPurchaseSpriteUrl(), SPRITE_CONTAINER_ID);
      }
    };
    const timeoutId = setTimeout(loadSprites, 10);
    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
    };
  }, []);

  const renderIcon = useCallback(purchaseFlowRenderIcon, []);

  const renderText = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const entry = TEXT_KEY_MAP[key];
      const i18nKey = typeof entry === 'function' ? entry() : (entry || key);
      return t(i18nKey, params as Record<string, unknown>);
    },
    [t]
  );

  return { renderIcon, renderText };
};
